<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useFeatureStore } from '@/stores/feature'
import { useThemeStore } from '@/stores/theme'
import BrandMark from '@/components/icons/BrandMark.vue'

const featureStore = useFeatureStore()
const themeStore = useThemeStore()

const isVisible = ref(false)
const isExiting = ref(false)
let exitTimer: ReturnType<typeof setTimeout> | null = null

// The splash sits on top of real content, so it is capped no matter what the
// admin panel is set to: a loading cue is worth a beat, not a wait.
// The exit is a curtain lifting off the page, so it gets a little longer.
const EXIT_MS = 520
const MAX_DURATION_MS = 900

const splashConfig = computed(() => featureStore.splashScreenConfig)
const durationMs = computed(() => Math.min(splashConfig.value?.durationMs ?? 600, MAX_DURATION_MS))

onMounted(() => {
  if (!featureStore.isSplashScreenEnabled) {
    isVisible.value = false
    return
  }

  if (splashConfig.value?.oncePerSession) {
    const hasSeen = sessionStorage.getItem('itorgo_splash_seen')
    if (hasSeen === 'true') {
      isVisible.value = false
      return
    }
  }

  isVisible.value = true
  // Page entrance animations (home hero) wait for the curtain to lift.
  document.documentElement.classList.add('is-splashing')

  exitTimer = setTimeout(() => {
    triggerExit()
  }, durationMs.value)
})

onUnmounted(() => {
  document.documentElement.classList.remove('is-splashing')
  if (exitTimer) clearTimeout(exitTimer)
})

function triggerExit() {
  if (isExiting.value) return
  isExiting.value = true
  document.documentElement.classList.remove('is-splashing')

  if (splashConfig.value?.oncePerSession) {
    try {
      sessionStorage.setItem('itorgo_splash_seen', 'true')
    } catch {
      // Ignore private mode quota error
    }
  }

  setTimeout(() => {
    isVisible.value = false
  }, EXIT_MS)
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isVisible"
      class="splash fixed inset-0 z-[999999] flex select-none items-center justify-center overflow-hidden bg-ink"
      :class="{ 'splash--exit pointer-events-none': isExiting }"
      role="status"
      aria-live="polite"
      aria-label="iTorgo"
    >
      <!-- Ala-Too dawn, the same scene the home hero opens with -->
      <div aria-hidden="true" class="absolute inset-0">
        <div class="sp-glow absolute left-1/2 top-[38%] h-[28rem] w-[28rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/30 blur-3xl" />
        <div class="sp-sun absolute left-1/2 bottom-[16%] h-44 w-44 -translate-x-1/2">
          <div class="absolute inset-0 rounded-full bg-sun/40 blur-2xl" />
          <div class="absolute inset-[24%] rounded-full bg-gradient-to-b from-sun to-sun/60" />
        </div>
        <svg class="sp-ridge sp-ridge--back absolute inset-x-0 bottom-0 h-[34%] w-full" viewBox="0 0 1200 300" preserveAspectRatio="none">
          <path d="M0 300 L0 170 L110 110 L190 150 L300 60 L380 120 L470 80 L560 150 L660 70 L760 130 L850 90 L960 160 L1060 100 L1200 150 L1200 300 Z" fill="rgb(var(--color-primary) / 0.25)" />
        </svg>
        <svg class="sp-ridge sp-ridge--front absolute inset-x-0 bottom-0 h-[22%] w-full" viewBox="0 0 1200 220" preserveAspectRatio="none">
          <path d="M0 220 L0 120 L140 60 L230 110 L350 40 L450 100 L560 70 L680 130 L790 50 L900 110 L1010 75 L1200 120 L1200 220 Z" fill="rgb(var(--color-ink-soft))" />
        </svg>
      </div>

      <div class="relative flex flex-col items-center gap-4">
        <BrandMark ground="dark" class="sp-mark h-16 w-16 p-2.5 shadow-xl" />
        <span class="sp-word font-display text-2xl font-bold tracking-tight text-white">{{ themeStore.theme.logoText || 'iTorgo' }}</span>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
/* Timeline (~0.8s): ridges rise, the sun comes up behind them, the mark pops
   and the wordmark settles; then the whole screen lifts like a curtain.
   Reduced motion: a plain fade, no movement. */
.splash {
  transition: opacity 0.3s ease;
}
.splash--exit {
  opacity: 0;
}
@media (prefers-reduced-motion: no-preference) {
  .splash {
    transition: transform 0.52s cubic-bezier(0.7, 0, 0.2, 1), opacity 0.52s ease;
  }
  .splash--exit {
    transform: translate3d(0, -100%, 0);
    opacity: 1;
  }
  .sp-glow { animation: sp-fade 0.8s ease-out both; }
  .sp-ridge { animation: sp-up 0.7s cubic-bezier(0.16, 1, 0.3, 1) both; }
  .sp-ridge--front { animation-delay: 0.08s; }
  .sp-sun { animation: sp-sun 0.9s cubic-bezier(0.16, 1, 0.3, 1) 0.1s both; }
  .sp-mark { animation: sp-pop 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s both; }
  .sp-word { animation: sp-up-small 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.34s both; }
}
@keyframes sp-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes sp-up { from { transform: translate3d(0, 3rem, 0); opacity: 0; } to { transform: none; opacity: 1; } }
@keyframes sp-sun {
  from { transform: translate3d(-50%, 6rem, 0) scale(0.8); opacity: 0; }
  to { transform: translate3d(-50%, 0, 0) scale(1); opacity: 1; }
}
@keyframes sp-pop { from { transform: scale(0.6); opacity: 0; } to { transform: scale(1); opacity: 1; } }
@keyframes sp-up-small { from { transform: translate3d(0, 0.6rem, 0); opacity: 0; } to { transform: none; opacity: 1; } }
</style>
